/**
 * PostComments 骨架屏组件
 *
 * CLS 优化：为帖子评论搜索结果提供布局稳定的骨架屏
 *
 * 问题：点击 Search Now 后，搜索结果区域从无到有突然出现，导致下方内容位移
 *
 * 解决：在 loading 时显示与真实内容布局一致的骨架屏，保持容器高度稳定
 */
import { Skeleton } from 'antd';
import { cn } from '@/lib/utils';

interface PostListSkeletonProps {
  /** 骨架项数量 */
  itemCount?: number;
}

export default function PostListSkeleton({ itemCount = 9 }: PostListSkeletonProps) {
  return (
    <div className="max-w-[984px] mx-auto">
      {/* InstagramAccountInfo 骨架 - 与真实组件布局一致 */}
      <div className="flex items-center justify-center gap-8 mt-8 max-md:flex-col max-md:gap-4 max-md:mt-5">
        {/* InstagramUserInfo 骨架：头像 + 昵称 + 用户名 */}
        <div className="flex items-center gap-4">
          <Skeleton.Avatar active size={48} shape="circle" />
          <div className="flex items-center gap-3">
            <Skeleton.Input active style={{ width: 120, height: 28 }} />
            <Skeleton.Input active style={{ width: 100, height: 16 }} />
          </div>
        </div>
        {/* 统计信息骨架：Post / Followers / Following */}
        <div className="flex items-center gap-8 text-base max-md:text-sm">
          <Skeleton.Input active style={{ width: 70, height: 20 }} />
          <Skeleton.Input active style={{ width: 100, height: 20 }} />
          <Skeleton.Input active style={{ width: 80, height: 20 }} />
        </div>
      </div>

      {/* 标题骨架：Latest posts from @username */}
      <div className="mt-8 mb-8 max-md:my-5 max-md:text-base">
        <Skeleton.Input active style={{ width: 250, height: 24 }} />
      </div>

      {/* PostInfoPannel 骨架：左侧帖子图片 + 右侧信息 */}
      <div className="flex gap-6 max-md:flex-col">
        {/* 左侧帖子图片 */}
        <div className="w-1/2 max-md:w-full aspect-square bg-gray-200 rounded-lg animate-pulse" />
        {/* 右侧信息区域 */}
        <div className="w-1/2 max-md:w-full flex flex-col gap-4">
          <Skeleton.Input active style={{ width: '60%', height: 24 }} />
          <Skeleton.Input active style={{ width: '100%', height: 36 }} />
          <div className="flex-1 flex flex-col gap-3 mt-4">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton.Avatar active size={40} />
                <div className="flex flex-col gap-1">
                  <Skeleton.Input active style={{ width: 120, height: 16 }} />
                  <Skeleton.Input active style={{ width: 100, height: 14 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SentimentBreakdown 骨架 */}
      <div className="mt-8 p-6 bg-gray-50 rounded-lg">
        <Skeleton.Input active style={{ width: 200, height: 24, marginBottom: 16 }} />
        <div className="flex gap-4 max-md:flex-col">
          <div className="flex-1 h-32 bg-gray-200 rounded animate-pulse" />
          <div className="flex-1 h-32 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>

      {/* AccountPerformance 骨架 */}
      <div className="mt-8 p-6 bg-gray-50 rounded-lg">
        <Skeleton.Input active style={{ width: 180, height: 24, marginBottom: 16 }} />
        <div className="h-48 bg-gray-200 rounded animate-pulse" />
      </div>

      {/* 帖子列表标题骨架 */}
      <div className="mt-12 mb-8 max-md:mt-9 max-md:mb-5">
        <Skeleton.Input active style={{ width: 220, height: 24 }} />
      </div>

      {/* 帖子网格骨架 */}
      <div className="mt-10 max-md:mt-5">
        <div className={cn('grid grid-cols-3 gap-4 max-md:grid-cols-2 max-md:gap-1.5')}>
          {Array.from({ length: itemCount }, (_, i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-md animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}
